import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  InputAdornment,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { toast } from "react-toastify";

const validationSchema = yup.object({
  mobileNumber: yup
    .string()
    .matches(/^\d{10}$/, "Enter a valid 10-digit mobile number")
    .required("Mobile number is required"),
  newPassword: yup.string().min(6).required("New password is required"),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("newPassword")], "Passwords must match")
    .required("Confirm password is required"),
});

function ForgotPasswordForm() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const formik = useFormik({
    initialValues: {
      mobileNumber: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema,
    onSubmit: (values) => {
      axios
        .post("http://localhost:5000/api/auth/reset-password", {
          mobileNumber: values.mobileNumber,
          otp: "123456", // in real case ask user for OTP
          newPassword: values.newPassword,
        })
        .then((res) => {
          setSuccess("Password has been reset successfully.");
          toast.success("Password reset successful!");
          setTimeout(() => {
            navigate("/");
          }, 2000);
        })
        .catch((err) => {
          setError(
            err?.response?.data?.message ||
              "Failed to reset password. Please try again."
          );
        });
    },
  });

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "#f5f5f5",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 400,
          background: "white",
          boxShadow: 3,
          borderRadius: 3,
          p: 4,
          textAlign: "center",
        }}
      >
        <Typography variant="h5" fontWeight="bold" mb={2}>
          Reset Your Password
        </Typography>
        <Typography fontSize="14px" color="text.secondary" mb={2}>
          Please enter your registered mobile number and choose a new password.
        </Typography>

        <form onSubmit={formik.handleSubmit}>
          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}

          <TextField
            fullWidth
            label="Mobile Number"
            name="mobileNumber"
            margin="normal"
            value={formik.values.mobileNumber}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.mobileNumber && Boolean(formik.errors.mobileNumber)
            }
            helperText={
              formik.touched.mobileNumber && formik.errors.mobileNumber
            }
          />

          <TextField
            fullWidth
            label="New Password"
            name="newPassword"
            type={showPassword ? "text" : "password"}
            margin="normal"
            value={formik.values.newPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.newPassword && Boolean(formik.errors.newPassword)
            }
            helperText={formik.touched.newPassword && formik.errors.newPassword}
            InputProps={{
              endAdornment: (
                <InputAdornment
                  position="end"
                  onClick={togglePasswordVisibility}
                  style={{ cursor: "pointer" }}
                >
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="Confirm Password"
            name="confirmPassword"
            type={showPassword ? "text" : "password"}
            margin="normal"
            value={formik.values.confirmPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.confirmPassword &&
              Boolean(formik.errors.confirmPassword)
            }
            helperText={
              formik.touched.confirmPassword && formik.errors.confirmPassword
            }
          />

          <Button
            fullWidth
            type="submit"
            variant="contained"
            sx={{
              mt: 2,
              backgroundColor: "#ffb320",
              textTransform: "capitalize",
              fontWeight: "bold",
              "&:hover": {
                backgroundColor: "#ffa000",
              },
            }}
          >
            Reset Password
          </Button>

          <Typography sx={{ mt: 2 }}>
            <span
              style={{ color: "blue", cursor: "pointer" }}
              onClick={() => navigate("/")}
            >
              Back to Login
            </span>
          </Typography>
        </form>
      </Box>
    </Box>
  );
}

export default ForgotPasswordForm;
